/**
 * Pure state transitions for the Quick Entry Service Date range's "All Dates" flag (AB#34852).
 *
 * Extracted into its own module (no imports at all) so the transition is unit-testable - the
 * component and mounted-list suites that would otherwise cover it cannot load in every
 * environment (node:stream-tainted import chain), same rationale as intakeFetchGate.ts and
 * intakeAnalysisOutcome.ts.
 *
 * Both directions mirror Classic Mode's InfoToRelease exactly (changeAllDates /
 * changeFromDate / changeToDate), so the two panes can never disagree about what a saved
 * release-info row means:
 *   - Checking All Dates clears both dates (an open-ended request has no range).
 *   - Unchecking it leaves the dates alone - they are already empty, and re-deriving them
 *     would be inventing data.
 *   - Picking a real date turns All Dates off (see nextRangeForServiceDate).
 */
export interface ServiceDateRange {
    readonly from?: Date;
    readonly to?: Date;
    readonly allDates: boolean;
}

/** The range after the specialist toggles the shared "All Dates" checkbox. */
export function nextRangeForAllDates(current: ServiceDateRange, checked: boolean): ServiceDateRange {
    return checked
        ? { from: undefined, to: undefined, allDates: true }
        : { from: current.from, to: current.to, allDates: false };
}

/**
 * The range after the specialist picks (or clears) one end of it. `date` undefined means the
 * field was cleared, which must NOT resurrect All Dates - it only leaves the flag as it was.
 */
export function nextRangeForServiceDate(
    current: ServiceDateRange,
    which: "from" | "to",
    date?: Date,
): ServiceDateRange {
    return {
        from: which === "from" ? date : current.from,
        to: which === "to" ? date : current.to,
        // An explicit date and "all dates" are mutually exclusive.
        allDates: date ? false : current.allDates,
    };
}
